import React, { useState, useEffect } from "react";
import { colors } from "../../../config/colors";
import AttachmentPreview from "./components/AttachmentPreview";
import { getJobTitleLabel } from "../../../utils/jobTitleLabel";

const JobChanges = ({ jobActivity, job }) => {
    const jobLabel = getJobTitleLabel();
    const [newStartTime, setNewStartTime] = useState("");
    const [existingStartTime, setExistingStartTime] = useState("");
    const [newEndTime, setNewEndTime] = useState("");
    const [existingEndTime, setExistingEndTime] = useState("");
    const [newNotes, setNewNotes] = useState("");
    const [existingNotes, setExistingNotes] = useState("");
    const [newAttachments, setNewAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [newTasks, setNewTasks] = useState([]);
    const [existingTasks, setExistingTasks] = useState([]);

    const getNotesDiff = (newNotes, existingNotes) => {
        let editedNewNotes = [];
        let editedExistingNotes = [];

        if (newNotes) {
            newNotes.map((note) => {
                let availableExistingNote = existingNotes.find(
                    (existingNote) => existingNote._id === note._id
                );
                if (availableExistingNote) {
                    if (availableExistingNote.content !== note.content) {
                        editedNewNotes.push(note);
                        editedExistingNotes.push(availableExistingNote);
                    }
                } else {
                    editedNewNotes.push(note);
                }
            });
        }

        return { editedNewNotes, editedExistingNotes };
    };

    const getAttachmentsDiff = (newAttachments, existingAttachments) => {
        let editedNewAttachments = [];
        let editedExistingAttachments = [];
        let deletedAttachments = [];
        let hasChanges = false;

        // First, find all attachments that were deleted
        if (existingAttachments) {
            existingAttachments.forEach((existingAttachment) => {
                const isDeleted = !newAttachments?.find(
                    (newAttachment) => newAttachment._id === existingAttachment._id
                );
                if (isDeleted) {
                    deletedAttachments.push(existingAttachment);
                    hasChanges = true;
                }
            });
        }

        // Then process new and modified attachments
        if (newAttachments) {
            newAttachments.forEach((attachment) => {
                let availableExistingAttachment = existingAttachments?.find(
                    (existingAttachment) => existingAttachment._id === attachment._id
                );
                if (availableExistingAttachment) {
                    if (
                        availableExistingAttachment.name !== attachment.name ||
                        availableExistingAttachment.url !== attachment.url
                    ) {
                        editedNewAttachments.push(attachment);
                        editedExistingAttachments.push(availableExistingAttachment);
                        hasChanges = true;
                    } else {
                        // If the attachment exists in both and hasn't changed, add it to both arrays
                        editedNewAttachments.push(attachment);
                        editedExistingAttachments.push(availableExistingAttachment);
                    }
                } else {
                    editedNewAttachments.push(attachment);
                    hasChanges = true;
                }
            });
        }

        return { editedNewAttachments, editedExistingAttachments, deletedAttachments, hasChanges };
    };

    const formatTime = (time) => {
        return new Date(time).toLocaleString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    };

    useEffect(() => {
        console.log(">>>> jobActivity: ", jobActivity);
        const jobActivityData = jobActivity.data;
        let newStartTimeChange = jobActivityData.newData?.actualStartTime;
        let existingStartTimeChange =
            jobActivityData.existingData?.actualStartTime;
        if (newStartTimeChange !== existingStartTimeChange) {
            // Convert to readable format
            newStartTimeChange = newStartTimeChange
                ? formatTime(newStartTimeChange)
                : "";

            existingStartTimeChange = existingStartTimeChange
                ? formatTime(existingStartTimeChange)
                : "";

            setNewStartTime(newStartTimeChange);
            setExistingStartTime(existingStartTimeChange);
        }

        let newEndTimeChange = jobActivityData.newData?.actualEndTime;
        let existingEndTimeChange = jobActivityData.existingData?.actualEndTime;
        if (newEndTimeChange !== existingEndTimeChange) {
            newEndTimeChange = newEndTimeChange
                ? formatTime(newEndTimeChange)
                : "";

            existingEndTimeChange = existingEndTimeChange
                ? formatTime(existingEndTimeChange)
                : "";

            setNewEndTime(newEndTimeChange);
            setExistingEndTime(existingEndTimeChange);
        }

        let availableNewNotes = jobActivityData.newData.notes;
        let availableExistingNotes = jobActivityData.existingData.notes;
        let { editedNewNotes, editedExistingNotes } = getNotesDiff(
            availableNewNotes,
            availableExistingNotes
        );

        let availableNewAttachments = jobActivityData.newData.workAttachments;
        let availableExistingAttachments =
            jobActivityData.existingData.workAttachments;
        let { editedNewAttachments, editedExistingAttachments, deletedAttachments, hasChanges } =
            getAttachmentsDiff(
                availableNewAttachments,
                availableExistingAttachments
            );

        let availableNewTasks = jobActivityData.newData.employeeTasks;
        let availableExistingTasks = jobActivityData.existingData.employeeTasks;
        let editedNewTasks = [];
        let editedExistingTasks = [];
        
        if (availableNewTasks) {
            availableNewTasks.forEach((task) => {
                let availableExistingTask = availableExistingTasks.find(
                    (existingTask) => existingTask._id === task._id
                );
                if (availableExistingTask) {
                    let edittedNewTask = {};
                    let edittedExistingTask = {};
                    let hasChanges = false;

                    // Check for status or timeSpent changes
                    if (
                        availableExistingTask.status !== task.status ||
                        availableExistingTask.timeSpent !== task.timeSpent
                    ) {
                        edittedNewTask = { ...task };
                        edittedExistingTask = { ...availableExistingTask };
                        hasChanges = true;
                    }

                    // Process task notes
                    let availableNewTaskNotes = task.notes;
                    let availableExistingTaskNotes = availableExistingTask.notes;
                    let { editedNewNotes, editedExistingNotes } = getNotesDiff(
                        availableNewTaskNotes,
                        availableExistingTaskNotes
                    );
                    if (editedNewNotes?.length > 0 || editedExistingNotes?.length > 0) {
                        if (!hasChanges) {
                            edittedNewTask = { ...task };
                            edittedExistingTask = { ...availableExistingTask };
                            hasChanges = true;
                        }
                        edittedNewTask.notes = editedNewNotes;
                        edittedExistingTask.notes = editedExistingNotes;
                    }

                    // Process task attachments with deletion tracking
                    let availableNewTaskAttachments = task.attachments;
                    let availableExistingTaskAttachments = availableExistingTask.attachments;
                    let { editedNewAttachments, editedExistingAttachments, deletedAttachments } =
                        getAttachmentsDiff(
                            availableNewTaskAttachments,
                            availableExistingTaskAttachments
                        );
                    
                    if (editedNewAttachments?.length > 0 || editedExistingAttachments?.length > 0 || deletedAttachments?.length > 0) {
                        if (!hasChanges) {
                            edittedNewTask = { ...task };
                            edittedExistingTask = { ...availableExistingTask };
                            hasChanges = true;
                        }
                        edittedNewTask.attachments = editedNewAttachments;
                        edittedExistingTask.attachments = [...editedExistingAttachments, ...deletedAttachments];
                    }

                    if (hasChanges) {
                        editedNewTasks.push(edittedNewTask);
                        editedExistingTasks.push(edittedExistingTask);
                    }
                } else {
                    editedNewTasks.push(task);
                }
            });
        }

        setNewNotes(editedNewNotes);
        setExistingNotes(editedExistingNotes);
        setNewAttachments(editedNewAttachments);
        setExistingAttachments([...editedExistingAttachments, ...deletedAttachments]);
        setNewTasks(editedNewTasks);
        setExistingTasks(editedExistingTasks);
    }, [jobActivity]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h4
                            className="text-xl font-semibold mb-2"
                            style={{ color: colors.primary.headingsBody }}
                        >
                            Change Details
                        </h4>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            Created{" "}
                            {new Date(jobActivity.createdAt).toLocaleString()}
                        </p>
                    </div>
                    <span
                        className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
                            jobActivity.isApproved === null
                                ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                                : jobActivity.isApproved
                                  ? "bg-green-50 text-green-700 border border-green-200"
                                  : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                    >
                        <span
                            className={`w-2 h-2 rounded-full ${
                                jobActivity.isApproved === null
                                    ? "bg-yellow-400"
                                    : jobActivity.isApproved
                                      ? "bg-green-400"
                                      : "bg-red-400"
                            }`}
                        ></span>
                        {jobActivity.isApproved === null
                            ? "Pending Approval"
                            : jobActivity.isApproved
                              ? "Approved"
                              : "Rejected"}
                    </span>
                </div>
            </div>

            <div className="p-6 space-y-8">
                {/* Time Changes Section */}
                {(newStartTime !== existingStartTime ||
                    newEndTime !== existingEndTime) && (
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="border-b border-gray-100 p-4">
                            <h5
                                className="text-lg font-semibold flex items-center gap-2"
                                style={{ color: colors.primary.headingsBody }}
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                Time Changes
                            </h5>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            <div className="p-4">
                                <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                        />
                                    </svg>
                                    Previous Times
                                </h6>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600">
                                            Start Time
                                        </span>
                                        <span className="text-sm font-medium">
                                            {existingStartTime || "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600">
                                            End Time
                                        </span>
                                        <span className="text-sm font-medium">
                                            {existingEndTime || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                        />
                                    </svg>
                                    New Times
                                </h6>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                        <span className="text-sm text-green-700">
                                            Start Time
                                        </span>
                                        <span className="text-sm font-medium text-green-700">
                                            {newStartTime || "N/A"}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                        <span className="text-sm text-green-700">
                                            End Time
                                        </span>
                                        <span className="text-sm font-medium text-green-700">
                                            {newEndTime || "N/A"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Task Changes Section */}
                {newTasks?.length > 0 && (
                    <div className="space-y-6">
                        <div className="flex items-center gap-2">
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                />
                            </svg>
                            <h5
                                className="text-lg font-semibold"
                                style={{ color: colors.primary.headingsBody }}
                            >
                                Task Changes
                            </h5>
                        </div>

                        {newTasks.map((newTask, index) => {
                            const existingTask = existingTasks[index];
                            const taskDetails = job.tasks.find(
                                (t) => t.taskId._id === newTask.taskId
                            );

                            if (!existingTask) return null;

                            const hasTaskAttachmentChanges = 
                                newTask.attachments?.length !== existingTask.attachments?.length ||
                                existingTask.attachments?.some(existing => 
                                    !newTask.attachments?.find(newAtt => newAtt._id === existing._id)
                                ) ||
                                newTask.attachments?.some(newAtt => 
                                    !existingTask.attachments?.find(existing => existing._id === newAtt._id)
                                );

                            return (
                                <div
                                    key={newTask._id}
                                    className="bg-white rounded-xl border border-gray-100 overflow-hidden"
                                >
                                    <div className="bg-gradient-to-r from-gray-50 to-white p-4 border-b border-gray-100">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                            <div>
                                                <h6
                                                    className="text-lg font-semibold"
                                                    style={{
                                                        color: colors.primary
                                                            .headingsBody,
                                                    }}
                                                >
                                                    {existingTask.taskDetails?.title || "Task"}
                                                </h6>
                                                <p className="text-sm text-gray-500">
                                                    Instance{" "}
                                                    {newTask.instanceIndex + 1}{" "}
                                                    of{" "}
                                                    {job.tasks.find(
                                                        (t) =>
                                                            t.taskId._id ===
                                                            newTask.taskId
                                                    )?.quantity || "N/A"}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        $
                                                        {
                                                            existingTask.taskDetails?.cost || "N/A"}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        Cost per unit
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                                        {/* Previous State */}
                                        <div className="p-4 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h6 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                    <svg
                                                        className="w-4 h-4 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                                        />
                                                    </svg>
                                                    Previous State
                                                </h6>
                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                                        existingTask.status ===
                                                        "COMPLETED"
                                                            ? "bg-green-100 text-green-800"
                                                            : existingTask.status ===
                                                                "IN_PROGRESS"
                                                              ? "bg-blue-100 text-blue-800"
                                                              : "bg-yellow-100 text-yellow-800"
                                                    }`}
                                                >
                                                    {existingTask.status}
                                                </span>
                                            </div>

                                            {/* <div className="p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-600">
                                                        Time Spent
                                                    </span>
                                                    <span className="text-sm font-medium">
                                                        {existingTask.timeSpent} minutes
                                                    </span>
                                                </div>
                                            </div> */}

                                            {hasTaskAttachmentChanges && existingTask?.attachments?.length > 0 && (
                                                <div className="space-y-2">
                                                    <h6 className="text-sm font-medium text-gray-700">
                                                        Attachments ({existingTask.attachments.length})
                                                    </h6>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {existingTask.attachments.map((attachment, i) => {
                                                            const isDeleted = !newTask.attachments?.find(
                                                                (newAttachment) => newAttachment._id === attachment._id
                                                            );
                                                            return (
                                                                <div key={i} className="relative">
                                                                    <AttachmentPreview
                                                                        attachment={attachment}
                                                                        className={isDeleted ? "opacity-50" : ""}
                                                                    />
                                                                    {isDeleted && (
                                                                        <div className="absolute top-2 right-2 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                                                                            Deleted
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {existingTask?.notes?.length >
                                                0 && (
                                                <div className="space-y-2">
                                                    <h6 className="text-sm font-medium text-gray-700">
                                                        Notes
                                                    </h6>
                                                    <div className="space-y-2">
                                                        {existingTask.notes.map(
                                                            (note, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="p-3 bg-gray-50 rounded-lg"
                                                                >
                                                                    <p className="text-sm text-gray-700">
                                                                        {
                                                                            note.content
                                                                        }
                                                                    </p>
                                                                    {note.createdAt && (
                                                                        <p className="text-xs text-gray-500 mt-2">
                                                                            {new Date(
                                                                                note.createdAt
                                                                            ).toLocaleString()}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* New State */}
                                        <div className="p-4 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h6 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                    <svg
                                                        className="w-4 h-4 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                    New State
                                                </h6>
                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                                        newTask.status ===
                                                        "COMPLETED"
                                                            ? "bg-green-100 text-green-800"
                                                            : newTask.status ===
                                                                "IN_PROGRESS"
                                                              ? "bg-blue-100 text-blue-800"
                                                              : "bg-yellow-100 text-yellow-800"
                                                    }`}
                                                >
                                                    {newTask.status}
                                                </span>
                                            </div>

                                            {/* <div className="p-3 bg-green-50 rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-green-700">
                                                        Time Spent
                                                    </span>
                                                    <span className="text-sm font-medium text-green-700">
                                                        {newTask.timeSpent} minutes
                                                    </span>
                                                </div>
                                            </div> */}

                                            {hasTaskAttachmentChanges && newTask?.attachments?.length > 0 && (
                                                <div className="space-y-2">
                                                    <h6 className="text-sm font-medium text-gray-700">
                                                        Remaining Attachments ({newTask.attachments.length})
                                                    </h6>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {newTask.attachments.map((attachment, i) => (
                                                            <AttachmentPreview
                                                                key={i}
                                                                attachment={attachment}
                                                                className="hover:bg-green-50 rounded-lg transition-colors duration-200"
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {newTask?.notes?.length > 0 && (
                                                <div className="space-y-2">
                                                    <h6 className="text-sm font-medium text-gray-700">
                                                        Notes
                                                    </h6>
                                                    <div className="space-y-2">
                                                        {newTask.notes.map(
                                                            (note, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="p-3 bg-green-50 rounded-lg"
                                                                >
                                                                    <p className="text-sm text-green-700">
                                                                        {
                                                                            note.content
                                                                        }
                                                                    </p>
                                                                    {note.createdAt && (
                                                                        <p className="text-xs text-green-600 mt-1">
                                                                            {new Date(
                                                                                note.createdAt
                                                                            ).toLocaleString()}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Notes Changes Section */}
                {(newNotes?.length > 0 || existingNotes?.length > 0) && (
                    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                        <div className="border-b border-gray-100 p-4">
                            <h5
                                className="text-lg font-semibold flex items-center gap-2"
                                style={{ color: colors.primary.headingsBody }}
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                </svg>
                                Notes Changes
                            </h5>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                            <div className="p-4">
                                <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                        />
                                    </svg>
                                    Previous Notes
                                </h6>
                                <div className="space-y-3">
                                    {existingNotes?.length > 0 ? (
                                        existingNotes.map((note, index) => (
                                            <div
                                                key={index}
                                                className="p-3 bg-gray-50 rounded-lg"
                                            >
                                                <p className="text-sm text-gray-700">
                                                    {note.content}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    {new Date(
                                                        note.createdAt
                                                    ).toLocaleString()}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                            No previous notes
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="p-4">
                                <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    <svg
                                        className="w-4 h-4 text-gray-400"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                        />
                                    </svg>
                                    New Notes
                                </h6>
                                <div className="space-y-3">
                                    {newNotes?.length > 0 ? (
                                        newNotes.map((note, index) => (
                                            <div
                                                key={index}
                                                className="p-3 bg-green-50 rounded-lg"
                                            >
                                                <p className="text-sm text-green-700">
                                                    {note.content}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                            No new notes
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Attachments Changes Section */}
                {(() => {
                    const hasAttachmentChanges = newAttachments?.length !== existingAttachments?.length || 
                        existingAttachments?.some(existing => 
                            !newAttachments?.find(newAtt => newAtt._id === existing._id)
                        ) ||
                        newAttachments?.some(newAtt => 
                            !existingAttachments?.find(existing => existing._id === newAtt._id)
                        );

                    return hasAttachmentChanges && (
                        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                            <div className="border-b border-gray-100 p-4">
                                <h5
                                    className="text-lg font-semibold flex items-center gap-2"
                                    style={{ color: colors.primary.headingsBody }}
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                        />
                                    </svg>
                                    {`${jobLabel} Attachments Changes`}
                                </h5>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                                <div className="p-4">
                                    <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                        <svg
                                            className="w-4 h-4 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                                            />
                                        </svg>
                                        Original Attachments ({existingAttachments?.length || 0})
                                    </h6>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {existingAttachments?.length > 0 ? (
                                            existingAttachments.map((attachment, index) => {
                                                const isDeleted = !newAttachments?.find(
                                                    (newAttachment) => newAttachment._id === attachment._id
                                                );
                                                return (
                                                    <div key={index} className="relative">
                                                        <AttachmentPreview
                                                            attachment={attachment}
                                                            className={isDeleted ? "opacity-50" : ""}
                                                        />
                                                        {isDeleted && (
                                                            <div className="absolute top-2 right-2 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                                                                Deleted
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="col-span-2 p-4 text-center text-sm text-gray-500">
                                                No original attachments
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="p-4">
                                    <h6 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                        <svg
                                            className="w-4 h-4 text-gray-400"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 5l7 7-7 7M5 5l7 7-7 7"
                                            />
                                        </svg>
                                        Remaining Attachments ({newAttachments?.length || 0})
                                    </h6>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {newAttachments?.length > 0 ? (
                                            newAttachments.map((attachment, index) => (
                                                <AttachmentPreview
                                                    key={index}
                                                    attachment={attachment}
                                                    className="hover:bg-green-50 rounded-lg transition-colors duration-200"
                                                />
                                            ))
                                        ) : (
                                            <div className="col-span-2 p-4 text-center text-sm text-gray-500">
                                                No remaining attachments
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default JobChanges;
